import { useEffect, useState } from "react";
import { Users, Store, Layers, Clock, Receipt, TrendingUp, Landmark, Flag } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatCard, Spinner, PageHeader } from "@/components/common";
import { api, naira } from "@/lib/api";
import { AdminTemplates, AdminUsers, AdminWithdrawals, AdminReports } from "@/pages/admin/AdminModeration";
import { AdminOrders, AdminTransactions, AdminCategories, AdminSettings, AdminEmails } from "@/pages/admin/AdminPlatform";

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard testid="admin-stat-users" label="Users" value={s.users} icon={Users} />
        <StatCard testid="admin-stat-sellers" label="Sellers" value={s.sellers} icon={Store} />
        <StatCard testid="admin-stat-live" label="Live templates" value={s.templates_live} icon={Layers} />
        <StatCard testid="admin-stat-pending" label="Pending review" value={s.templates_pending} icon={Clock} accent="text-amber-500" />
        <StatCard testid="admin-stat-orders" label="Paid orders" value={s.orders_paid} icon={Receipt} />
        <StatCard testid="admin-stat-gmv" label="Gross sales" value={naira(s.gmv)} icon={TrendingUp} />
        <StatCard testid="admin-stat-revenue" label="Platform revenue" value={naira(s.platform_revenue)} icon={TrendingUp} accent="text-emerald-500" />
        <StatCard testid="admin-stat-withdrawals" label="Pending payouts" value={`${s.withdrawals_pending} · ${naira(s.withdrawals_pending_amount)}`} icon={Landmark} accent="text-primary" />
      </div>
      <div className="card-surface mt-6 p-5">
        <div className="mb-4 flex items-center justify-between"><p className="font-semibold">Platform revenue · 30 days</p><span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Flag size={12} />{s.reports_open} open reports</span></div>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={s.chart}>
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={45} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} formatter={(v) => naira(v)} />
              <Area type="monotone" dataKey="gmv" stroke="hsl(38 92% 55%)" fill="hsl(38 92% 55% / 0.15)" />
              <Area type="monotone" dataKey="revenue" stroke="hsl(262 83% 62%)" fill="hsl(262 83% 62% / 0.25)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const TABS = [
  ["overview", "Overview", Overview], ["templates", "Templates", AdminTemplates], ["users", "Users", AdminUsers], ["orders", "Orders", AdminOrders],
  ["transactions", "Transactions", AdminTransactions], ["withdrawals", "Withdrawals", AdminWithdrawals], ["reports", "Reports", AdminReports],
  ["categories", "Categories", AdminCategories], ["settings", "Settings", AdminSettings], ["emails", "Emails", AdminEmails],
];

export default function AdminDashboard() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <PageHeader eyebrow="Control room" title="Admin Dashboard" />
      <Tabs defaultValue="overview">
        <div className="no-scrollbar -mx-4 overflow-x-auto px-4">
          <TabsList className="h-auto w-max">
            {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="px-3 py-2">{l}</TabsTrigger>)}
          </TabsList>
        </div>
        {TABS.map(([v, , C]) => <TabsContent key={v} value={v} className="mt-6"><C /></TabsContent>)}
      </Tabs>
    </div>
  );
}
